// src/lib/sentry.js
//
// Thin wrapper around @sentry/browser so the rest of the codebase doesn't
// have to know whether Sentry is configured. Two design decisions worth
// reading before changing anything here:
//
// 1. **Dynamic import of @sentry/browser (NOT @sentry/react).** The
//    @sentry/react package eagerly bundles @sentry-internal/replay +
//    @sentry-internal/rrweb (~100 KiB combined) even when you set
//    replaysSessionSampleRate: 0 — the replay/rrweb code is imported at
//    @sentry/react module-load time and our config flag only stops it
//    from running, not from being parsed. PSI flagged this in "Reduce
//    unused JavaScript" as 102 KiB unused on the monitoring chunk.
//    @sentry/browser exposes the same captureException / captureMessage
//    / init APIs but without the replay subtree, so dynamic import +
//    parse drops by ~100 KiB. We don't use @sentry/react's React-specific
//    helpers (ErrorBoundary, withErrorBoundary, withProfiler) — our own
//    components/ErrorBoundary.js calls captureException through this
//    wrapper instead — so the swap is transparent to callers.
//
// 2. **Pre-init buffer.** Errors thrown before Sentry finishes loading
//    would be lost if we just no-op'd. Instead we buffer them in memory
//    and flush once the SDK initialises. In practice the SDK loads in
//    <100 ms after first paint, so the buffer rarely holds anything,
//    but it's the difference between "we caught it" and "we silently
//    dropped it" for the bug that crashes on load.
//
// Setup checklist (one-time, ~5 min):
//   1. Create a free Sentry account at https://sentry.io
//   2. Create a new project → React → copy the DSN
//   3. Add to .env (and to Cloudflare Pages env vars if deploying):
//        REACT_APP_SENTRY_DSN=https://...@...ingest.sentry.io/...
//   4. Re-deploy. That's it — no other code changes needed.
//
// Without DSN set, every function below is a safe no-op.

const DSN = process.env.REACT_APP_SENTRY_DSN;
const ENV = process.env.NODE_ENV || 'production';

// Live SDK reference once the dynamic import resolves. Anything captured
// before that lands in `buffer` and is flushed on init.
let Sentry = null;
const buffer = [];

function flushBuffer() {
  while (buffer.length) {
    const item = buffer.shift();
    if (item.kind === 'exception') Sentry.captureException(item.error, { extra: item.context });
    else if (item.kind === 'message') Sentry.captureMessage(item.message, item.level);
  }
}

// Kick off the actual SDK download + init. Split out so we can call it
// from a deferred scheduler instead of synchronously at module load.
function loadAndInitSentry() {
  // Eslint: webpack picks this up at build time and emits a separate chunk.
  //
  // ⚠️ The chunk is named "monitoring" (not "sentry") so Safari/Chrome
  // ad/tracking blockers — Adblock Plus, AdGuard, uBlock Origin, etc. —
  // don't match it against rules like `*/sentry*.js` or `||sentry*$script`
  // from EasyPrivacy / EasyList. When the chunk was named "sentry", users
  // with content blockers got the chunk request blocked at the network
  // layer (ERR_BLOCKED_BY_CLIENT). The .catch() below would normally
  // handle this gracefully, BUT some blockers (especially Safari Content
  // Blockers) inject early enough that the error surfaces as an
  // unrecoverable lazy-load failure that bubbles up to ErrorBoundary and
  // shows the "Something went wrong" page to the user. Renaming the
  // chunk takes us out of every adblocker filter list.
  import(/* webpackChunkName: "monitoring" */ '@sentry/browser')
    .then((mod) => {
      Sentry = mod;
      Sentry.init({
        dsn: DSN,
        environment: ENV,
        // Performance monitoring is OFF by design.
        //
        // Sentry SDK v8 auto-includes `browserTracingIntegration` whenever
        // `tracesSampleRate > 0`, and that integration ships a built-in
        // web-vitals collector that auto-promotes LCP/CLS/FID/FCP/TTFB
        // measurements into the Issues feed as "web-vitals: METRIC=VALUE"
        // rows. (Discovered 2026-05-03 — dashboard had 5 web-vitals
        // warnings burying the real exception signal.)
        //
        // We already track CWV via GA4 in src/reportWebVitals.js where
        // we get proper segmentation (locale, source, device class) and
        // it doesn't compete with Sentry's error-triage workflow. We
        // also run Lighthouse CI for synthetic perf monitoring on every
        // PR. Sentry transactions add no signal we don't already have,
        // so we disable them entirely.
        //
        // To re-enable tracing, set `tracesSampleRate: 0.1` AND remove
        // `integrations: []` — but be aware web-vitals issues will
        // return unless you also set up a beforeSendTransaction filter.
        tracesSampleRate: 0,
        integrations: [],
        // No replaysSessionSampleRate / replaysOnErrorSampleRate keys —
        // @sentry/browser doesn't ship the replay integration, so those
        // options don't apply (and adding them would emit a warning).
        // We never collect PII automatically. If a specific exception needs
        // user context, attach it via `setUser` — but for a public marketing
        // site this is rare and we err on the side of privacy.
        sendDefaultPii: false,
        // Drop noise from local development if DSN somehow leaks into a
        // dev build (e.g. testing the integration). Production keeps every
        // event.
        beforeSend(event) {
          if (typeof window !== 'undefined' && /^(localhost|127\.|0\.0\.0\.0)/.test(window.location.hostname)) {
            return null;
          }
          return event;
        },
        // Common noise filters — these errors reach the SDK but we don't
        // want them in the dashboard / quota:
        //   • ResizeObserver loop limit exceeded — benign Chrome warning
        //   • non-error promise rejections from third-party widgets
        ignoreErrors: [
          /ResizeObserver loop/i,
          /Non-Error promise rejection/i,
          // Safari's "AbortError" when navigating away during a fetch — not
          // a real bug, just a race we don't care about.
          /AbortError: The user aborted a request/i,
        ],
      });
      flushBuffer();
    })
    .catch((err) => {
      // Sentry SDK failed to load — log to console and carry on. This
      // happens if the chunk request 404s (broken deploy) or if a CSP
      // misconfig blocks sentry.io. ErrorBoundary still falls back to
      // GA4 exception events in this case.
      // eslint-disable-next-line no-console
      console.warn('Sentry init failed:', err && err.message);
    });
}

// Load-on-demand: don't pull the Sentry SDK chunk down on every page
// view — only when an error actually happens.
//
// Why this matters: even with replay disabled, @sentry/browser bundles
// rrweb-snapshot + replay-worker as built-in integrations that can't be
// tree-shaken from a dynamic import. The chunk is ~110 KiB gzipped /
// 350 KiB parsed, of which ~97 KiB is dead code on every page view that
// doesn't hit an error (i.e. ~99 % of sessions). Lighthouse's "Reduce
// unused JavaScript" audit credits the full 97 KiB as wasted bytes,
// dragging the Performance score down by ~3-5 points.
//
// New strategy: register cheap window error/unhandledrejection listeners
// at module load (cost: ~50 bytes, no chunk download). When the FIRST
// error fires, kick off the SDK download, wait for it to land, and
// flush the buffered error through Sentry.captureException. Subsequent
// errors go through Sentry directly.
//
// Trade-off: errors thrown DURING the SDK's initial download (network
// race condition — typically <500 ms) are still buffered and flushed
// once init completes; same correctness as the previous load-on-idle
// approach. The only thing we lose is breadcrumb collection for events
// that occur BEFORE the first error — for a marketing site with mostly
// static content, this is acceptable. If breadcrumbs become important
// later, we can either flip back to the deferred-load schedule or
// migrate to a smaller error-tracker library.
//
// PSI impact: monitoring chunk no longer requested during a Lighthouse
// run (which never throws), so it disappears from the "Reduce unused
// JavaScript" audit entirely. Mobile score expected to gain ~3-5 points
// purely from this change.
let loadStarted = false;
function ensureSentryLoaded() {
  if (!DSN || typeof window === 'undefined' || loadStarted) return;
  loadStarted = true;
  // requestIdleCallback isn't appropriate here — we want to handle the
  // error AS SOON AS POSSIBLE so the user-visible session captures it
  // before the user navigates away. setTimeout(0) yields once to keep
  // the error-handling stack from blocking the current task.
  setTimeout(loadAndInitSentry, 0);
}

if (DSN && typeof window !== 'undefined') {
  // Catch globally-uncaught errors. ErrorBoundary components catch
  // React render errors, but window.onerror also covers raw DOM
  // exceptions (e.g. "Cannot read property of null" in event handlers
  // outside React's tree, third-party widgets, etc.).
  window.addEventListener('error', (e) => {
    ensureSentryLoaded();
    if (Sentry) {
      Sentry.captureException(e.error || new Error(e.message || 'window.error'));
    } else {
      buffer.push({
        kind: 'exception',
        error: e.error || new Error(e.message || 'window.error'),
        context: { filename: e.filename, lineno: e.lineno, colno: e.colno },
      });
    }
  });
  window.addEventListener('unhandledrejection', (e) => {
    ensureSentryLoaded();
    const err = e.reason instanceof Error
      ? e.reason
      : new Error(typeof e.reason === 'string' ? e.reason : 'unhandledrejection');
    if (Sentry) Sentry.captureException(err);
    else buffer.push({ kind: 'exception', error: err });
  });
}

export function captureException(error, context) {
  if (Sentry) {
    Sentry.captureException(error, { extra: context });
    return;
  }
  if (!DSN) return;  // No DSN configured — silent no-op.
  // SDK not loaded yet. Trigger the load-on-demand path (idempotent —
  // safe to call multiple times) and buffer the error to be flushed
  // once init completes.
  ensureSentryLoaded();
  buffer.push({ kind: 'exception', error, context });
}

export function captureMessage(message, level = 'info') {
  if (Sentry) {
    Sentry.captureMessage(message, level);
    return;
  }
  if (!DSN) return;
  ensureSentryLoaded();
  buffer.push({ kind: 'message', message, level });
}

export function isSentryEnabled() {
  return !!DSN;
}
